import { hasResource } from '@ctrl/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Contact, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CheckboxField, SelectField, TextField } from '@/components/form-fields';
import { useDetailTitle } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth';
import {
  addCustomerNote,
  type CustomerDetail,
  type CustomerProfilePatch,
  getCustomerRecord,
  setCustomerTag,
  updateCustomer,
} from '@/lib/customers';
import { useForm } from '@/lib/use-form';

/**
 * One customer.
 *
 * Read through the browser client under RLS for the same reason the list is:
 * this is shopper PII and the `customers` capability is enforced per request,
 * so nothing about this person is in the console until somebody entitled to
 * them asks for this id.
 *
 * `customer_type` here is what the shop DECLARES them as. The storefront
 * prices on a verified, unexpired document instead -- which is why the
 * documents panel sits next to the type control rather than on another page.
 */
export const Route = createFileRoute('/customers/$customerId')({
  component: CustomersDetailPage,
});

const profileSchema = z.object({
  firstName: z.string().trim().max(80),
  lastName: z.string().trim().max(80),
  email: z.string().trim().email('Enter a valid email').or(z.literal('')),
  phone: z.string().trim().max(40),
  customerType: z.enum(['recreational', 'medical', 'state_medical']),
  birthday: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .or(z.literal('')),
  medicalCardExpiresAt: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .or(z.literal('')),
  marketingOptIn: z.boolean(),
  storeCredit: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Amounts look like 12.50'),
});

type ProfileValues = z.infer<typeof profileSchema>;

const TYPE_OPTIONS = [
  { value: 'recreational', label: 'Recreational' },
  { value: 'medical', label: 'Medical patient' },
  { value: 'state_medical', label: 'State medical patient' },
];

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const date = (value: string | null) =>
  value ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value)) : '—';

function toValues(customer: CustomerDetail): ProfileValues {
  return {
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    email: customer.email ?? '',
    phone: customer.phone ?? '',
    customerType: customer.customerType as ProfileValues['customerType'],
    birthday: customer.birthday ?? '',
    medicalCardExpiresAt: customer.medicalCardExpiresAt ?? '',
    marketingOptIn: customer.marketingOptIn,
    storeCredit: (customer.storeCreditCents / 100).toFixed(2),
  };
}

function toPatch(values: ProfileValues): CustomerProfilePatch {
  return {
    firstName: values.firstName || null,
    lastName: values.lastName || null,
    email: values.email || null,
    phone: values.phone || null,
    customerType: values.customerType,
    birthday: values.birthday || null,
    medicalCardExpiresAt: values.medicalCardExpiresAt || null,
    marketingOptIn: values.marketingOptIn,
    storeCreditCents: Math.round(Number(values.storeCredit) * 100),
  };
}

function CustomersDetailPage() {
  const { customerId } = useParams({ from: '/customers/$customerId' });
  const { activeAccountId, claims, session } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  const record = useQuery({
    queryKey: ['customer', accountId, customerId],
    queryFn: () => getCustomerRecord(accountId, customerId),
    enabled: Boolean(accountId),
  });

  const customer = record.data?.customer;

  // Publishes the third breadcrumb: [collapse | Customers | this record].
  const name = customer
    ? [customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
      customer.email ||
      'Customer'
    : `Customer ${customerId.slice(0, 8)}`;

  useDetailTitle(name);

  /*
   * Editing a customer is `customers` + the table's own update policy, which
   * any member of the account satisfies; `customers.manage` gates deletion and
   * tags, not this form.
   */
  const canEdit = accountId ? hasResource(claims, accountId, 'customers') : false;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['customer', accountId, customerId] });
    queryClient.invalidateQueries({ queryKey: ['customers', accountId] });
  };

  if (record.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  /*
   * One answer for "no such customer" and "not yours": RLS filters the row out
   * either way, and confirming one exists elsewhere is itself a disclosure.
   */
  if (!record.data || !customer) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-3 py-24 text-center">
        <Contact className="size-8" />
        <p className="text-foreground text-sm font-medium">No such customer</p>
        <p className="max-w-sm text-sm">
          They may have been deleted, or they belong to another account.
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/customers">
            <ArrowLeft className="size-4" />
            Back to customers
          </Link>
        </Button>
      </div>
    );
  }

  const { addresses, orders, notes, documents, consents, loyalty, tags, assignedTagIds } =
    record.data;

  const assigned = new Set(assignedTagIds);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">{name}</h1>
          <Badge variant="secondary">
            {TYPE_OPTIONS.find((option) => option.value === customer.customerType)?.label ??
              customer.customerType}
          </Badge>
          {customer.hasAccount && <Badge variant="outline">Has an account</Badge>}
        </div>
        <p className="text-muted-foreground text-sm">
          {[customer.email, customer.phone].filter(Boolean).join(' · ') || 'No contact details'}
          {' · '}Customer since {date(customer.createdAt)}
        </p>
      </div>

      <dl className="grid gap-3 sm:grid-cols-4">
        <Stat label="Orders" value={String(customer.totalOrders)} />
        <Stat label="Lifetime spend" value={usd(customer.totalSpentCents)} />
        <Stat label="Store credit" value={usd(customer.storeCreditCents)} />
        <Stat
          label="Loyalty points"
          value={loyalty ? String(loyalty.balancePoints) : 'Not enrolled'}
        />
      </dl>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="addresses">Addresses ({addresses.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <ProfileForm
            customer={customer}
            canEdit={canEdit}
            onSave={async (patch) => {
              await updateCustomer(accountId, customer.id, patch);
              refresh();
            }}
          />

          <section className="mt-6 rounded-lg border p-4">
            <h2 className="text-sm font-medium">Tags</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Segments and campaigns read these. Managing the tag list itself needs
              &ldquo;customers.manage&rdquo;.
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {tags.length ? (
                tags.map((tag) => (
                  <Button
                    key={tag.id}
                    type="button"
                    size="sm"
                    variant={assigned.has(tag.id) ? 'default' : 'outline'}
                    disabled={!canEdit}
                    onClick={async () => {
                      try {
                        await setCustomerTag(accountId, {
                          customerId: customer.id,
                          tagId: tag.id,
                          assigned: !assigned.has(tag.id),
                        });
                        refresh();
                      } catch (error) {
                        toast.error((error as Error).message);
                      }
                    }}
                  >
                    {tag.name}
                  </Button>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No tags defined yet.</p>
              )}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          {orders.length ? (
            <ul className="divide-y rounded-lg border">
              {orders.map((order) => (
                <li key={order.id} className="flex items-center justify-between gap-4 p-3 text-sm">
                  <div>
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: order.id }}
                      className="font-medium hover:underline"
                    >
                      #{order.orderNumber}
                    </Link>
                    <p className="text-muted-foreground text-xs">
                      {date(order.placedAt)} · {order.fulfillmentType.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{usd(order.totalCents)}</p>
                    <p className="text-muted-foreground text-xs">
                      {order.status.replace(/_/g, ' ')} · {order.paymentStatus}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No orders yet.</Empty>
          )}
        </TabsContent>

        <TabsContent value="addresses" className="mt-4">
          {addresses.length ? (
            <ul className="grid gap-3 sm:grid-cols-2">
              {addresses.map((address) => (
                <li key={address.id} className="rounded-lg border p-4 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium">{address.label || 'Address'}</p>
                    {address.isDefault && <Badge variant="secondary">Default</Badge>}
                  </div>
                  <p className="text-muted-foreground mt-1">
                    {address.line1}
                    {address.line2 ? `, ${address.line2}` : ''}
                    <br />
                    {address.city}, {address.state} {address.zip}
                  </p>
                  {address.deliveryInstructions && (
                    <p className="text-muted-foreground mt-1 text-xs">
                      {address.deliveryInstructions}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No saved addresses.</Empty>
          )}
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <NoteComposer
            disabled={!canEdit}
            onAdd={async (body) => {
              await addCustomerNote(accountId, {
                customerId: customer.id,
                body,
                authorId: session?.user.id ?? null,
                authorName:
                  (session?.user.user_metadata?.name as string | undefined) ??
                  session?.user.email ??
                  null,
              });
              refresh();
            }}
          />

          {notes.length ? (
            <ul className="mt-4 space-y-3">
              {notes.map((note) => (
                <li key={note.id} className="rounded-lg border p-3 text-sm">
                  <p className="whitespace-pre-wrap">{note.body}</p>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {note.authorName ?? 'Staff'} · {date(note.createdAt)}
                    {note.pinned ? ' · pinned' : ''}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <Empty className="mt-4">No notes on this customer.</Empty>
          )}
        </TabsContent>

        <TabsContent value="compliance" className="mt-4 space-y-6">
          <section>
            <h2 className="text-sm font-medium">Identification</h2>
            {documents.length ? (
              <ul className="mt-2 divide-y rounded-lg border">
                {documents.map((document) => (
                  <li
                    key={document.id}
                    className="flex items-center justify-between gap-4 p-3 text-sm"
                  >
                    <div>
                      <p className="font-medium">{document.type.replace(/_/g, ' ')}</p>
                      <p className="text-muted-foreground text-xs">
                        {document.idNumber ? `${document.idNumber} · ` : ''}
                        Expires {date(document.expirationDate)}
                      </p>
                    </div>
                    <Badge variant={document.status === 'verified' ? 'default' : 'secondary'}>
                      {document.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty className="mt-2">
                No documents on file, so this customer is priced as recreational.
              </Empty>
            )}
          </section>

          <section>
            <h2 className="text-sm font-medium">Marketing consent</h2>
            {consents.length ? (
              <ul className="mt-2 divide-y rounded-lg border">
                {consents.map((consent) => (
                  <li
                    key={consent.id}
                    className="flex items-center justify-between gap-4 p-3 text-sm"
                  >
                    <span className="capitalize">{consent.channel}</span>
                    <span className="text-muted-foreground text-xs">
                      {consent.granted ? 'Granted' : 'Withdrawn'} via {consent.source} ·{' '}
                      {date(consent.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty className="mt-2">
                No consent records; the checkout opt-in is the only signal.
              </Empty>
            )}
          </section>

          {loyalty && (
            <section>
              <h2 className="text-sm font-medium">Loyalty</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {loyalty.balancePoints} points · {loyalty.lifetimeEarned} earned ·{' '}
                {loyalty.lifetimeRedeemed} redeemed
              </p>
              {loyalty.transactions.length > 0 && (
                <ul className="mt-2 divide-y rounded-lg border">
                  {loyalty.transactions.map((entry) => (
                    <li
                      key={entry.seq}
                      className="flex items-center justify-between gap-4 p-3 text-sm"
                    >
                      <div>
                        <p className="capitalize">{entry.kind.replace(/_/g, ' ')}</p>
                        <p className="text-muted-foreground text-xs">
                          {entry.description ?? '—'} · {date(entry.createdAt)}
                        </p>
                      </div>
                      <span className="tabular-nums">
                        {entry.points > 0 ? `+${entry.points}` : entry.points} →{' '}
                        {entry.balanceAfter}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-lg font-semibold">{value}</dd>
    </div>
  );
}

function Empty({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-muted-foreground rounded-lg border border-dashed p-6 text-sm ${className}`}>
      {children}
    </p>
  );
}

function ProfileForm({
  customer,
  canEdit,
  onSave,
}: {
  customer: CustomerDetail;
  canEdit: boolean;
  onSave: (patch: CustomerProfilePatch) => Promise<void>;
}) {
  const { values, errors, isDirty, set, reset, submit } = useForm<ProfileValues>(
    toValues(customer),
    profileSchema,
  );

  // A refetch after saving (or a change made elsewhere) rebases the form.
  useEffect(() => {
    reset(toValues(customer));
  }, [customer, reset]);

  const save = useMutation({
    mutationFn: (parsed: ProfileValues) => onSave(toPatch(parsed)),
    onSuccess: () => toast.success('Customer updated.'),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="max-w-3xl space-y-6"
    >
      {!canEdit && (
        <p className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm">
          <Lock className="size-4 shrink-0" />
          You can see this customer but not change them. That needs the customers capability.
        </p>
      )}

      <fieldset disabled={!canEdit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="customer-first-name"
            label="First name"
            value={values.firstName}
            error={errors.firstName}
            onChange={(value) => set('firstName', value)}
          />
          <TextField
            id="customer-last-name"
            label="Last name"
            value={values.lastName}
            error={errors.lastName}
            onChange={(value) => set('lastName', value)}
          />
          <TextField
            id="customer-email"
            label="Email"
            type="email"
            value={values.email}
            error={errors.email}
            onChange={(value) => set('email', value)}
          />
          <TextField
            id="customer-phone"
            label="Phone"
            type="tel"
            value={values.phone}
            error={errors.phone}
            onChange={(value) => set('phone', value)}
          />
          <TextField
            id="customer-birthday"
            label="Date of birth"
            type="date"
            value={values.birthday}
            error={errors.birthday}
            onChange={(value) => set('birthday', value)}
          />
          <SelectField
            id="customer-type"
            label="Declared type"
            description="What they are priced as still depends on a verified, unexpired card."
            value={values.customerType}
            options={TYPE_OPTIONS}
            error={errors.customerType}
            onChange={(value) => set('customerType', value as ProfileValues['customerType'])}
          />
          <TextField
            id="customer-med-expiry"
            label="Medical card expires"
            type="date"
            value={values.medicalCardExpiresAt}
            error={errors.medicalCardExpiresAt}
            onChange={(value) => set('medicalCardExpiresAt', value)}
          />
          <TextField
            id="customer-store-credit"
            label="Store credit"
            description="Dollars. Spent automatically at the register."
            value={values.storeCredit}
            error={errors.storeCredit}
            onChange={(value) => set('storeCredit', value)}
          />
        </div>

        <CheckboxField
          id="customer-marketing"
          label="Opted in to marketing email"
          checked={values.marketingOptIn}
          onChange={(checked) => set('marketingOptIn', checked)}
        />

        <div className="flex gap-2">
          <Button type="submit" disabled={!isDirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {isDirty && (
            <Button type="button" variant="ghost" onClick={() => reset()}>
              Discard
            </Button>
          )}
        </div>
      </fieldset>
    </form>
  );
}

function NoteComposer({
  disabled,
  onAdd,
}: {
  disabled: boolean;
  onAdd: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');

  const add = useMutation({
    mutationFn: () => onAdd(body.trim()),
    onSuccess: () => {
      setBody('');
      toast.success('Note added.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (body.trim()) add.mutate();
      }}
      className="space-y-2"
    >
      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="What should the next person to serve them know?"
        rows={3}
        disabled={disabled}
      />
      <Button type="submit" size="sm" disabled={disabled || !body.trim() || add.isPending}>
        Add note
      </Button>
    </form>
  );
}
